import { useState } from 'react';
import { ArrowRightLeft, MapPin } from 'lucide-react';
import type { SubdivisionStockRow } from '@eombor/contracts';
import { Button, Dialog } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { useMoveLot } from '../model/subdivision.queries.js';
import { StoreroomPicker, type StoreroomPlace } from './StoreroomPicker.js';

/** Lotning joyi matn bo'lib: «Garaj · Seyf». */
export const placeText = (row: Pick<SubdivisionStockRow, 'storeroomName' | 'zoneName'>): string =>
  row.zoneName === null ? row.storeroomName : `${row.storeroomName} · ${row.zoneName}`;

/**
 * KO'CHIRISH — lot butunligicha boshqa ombor yoki zonaga o'tadi.
 * Hozirgi joy tanlab bo'lmaydi: «hech narsa o'zgarmadi» degan so'rovni
 * serverga yuborib, 400 olishdan ko'ra tugmani o'chirib qo'ygan ma'qul.
 */
export const MoveLotDialog = ({ row, onClose }: { row: SubdivisionStockRow; onClose: () => void }) => {
  const current: StoreroomPlace = { storeroomId: row.storeroomId, zoneId: row.zoneId };
  const [place, setPlace] = useState<StoreroomPlace | null>(null);
  const move = useMoveLot();

  const same =
    place === null || (place.storeroomId === current.storeroomId && place.zoneId === current.zoneId);

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('storeroom.moveTitle')}
      description={t('storeroom.moveHint', { name: row.nomenclatureName })}
      size="lg"
      busy={move.isPending}
      error={move.error === null ? undefined : move.error.message}
      footer={
        <div className="flex w-full flex-wrap items-center justify-end gap-3">
          {same && place !== null ? (
            <span className="mr-auto text-sm text-text-muted">{t('storeroom.moveSame')}</span>
          ) : null}
          <Button variant="ghost" onClick={onClose} disabled={move.isPending}>
            {t('storeroom.cancel')}
          </Button>
          <Button
            loading={move.isPending}
            disabled={same}
            onClick={() => {
              if (place === null) return;
              move.mutate({ lotId: row.lotId, input: place }, { onSuccess: onClose });
            }}
          >
            <ArrowRightLeft size={16} aria-hidden />
            {t('storeroom.moveSubmit')}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="flex flex-wrap items-center gap-2 rounded-[var(--radius-sm)] border border-border bg-surface-2 px-3 py-2 text-sm">
          <span className="text-text-muted">{t('storeroom.moveFrom')}:</span>
          <MapPin size={14} aria-hidden className="text-accent" />
          <span className="font-medium text-text">{placeText(row)}</span>
        </p>
        <StoreroomPicker value={place} onChange={setPlace} current={current} title={t('storeroom.moveTitle')} hint=" " />
      </div>
    </Dialog>
  );
};
